import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/Toast';
import { socialApi } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/queries';
import type { FriendRequest } from '@/lib/api/types';
import { t } from '@/lib/i18n';

/**
 * Aceitar ou recusar um pedido, com o mesmo conjunto de invalidações nos dois lugares que
 * oferecem a decisão: a página de Amigos no celular e a lateral da timeline no desktop.
 *
 * `working` devolve o id em andamento — e não um booleano — porque a lista mostra vários
 * pedidos ao mesmo tempo e só a linha tocada deve ficar ocupada.
 */
export function useRespondToRequest(): {
  working: string | null;
  respond: (request: FriendRequest, accept: boolean) => Promise<void>;
} {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [working, setWorking] = useState<string | null>(null);

  const respond = async (request: FriendRequest, accept: boolean) => {
    setWorking(request.id);
    try {
      if (accept) await socialApi.accept(request.id);
      else await socialApi.decline(request.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.friendRequests }),
        queryClient.invalidateQueries({ queryKey: qk.friends }),
        // A timeline muda no mesmo instante: aceitar traz as voltas da pessoa para o feed.
        queryClient.invalidateQueries({ queryKey: ['feed'] }),
        queryClient.invalidateQueries({ queryKey: ['users', 'search'] }),
      ]);
      toast.success(accept ? t('friends.nowFriends') : t('friends.requestDeclined'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('friends.respondFailed'));
    } finally {
      setWorking(null);
    }
  };

  return { working, respond };
}
